import { Injectable } from "@angular/core";
import { LinkDeviceEndTransmissionService } from "./link-device-end-transmission.service";
import { EndInputs, EndPortLink, SelectedEnd } from "../link-device-model";
import { Port, Slot, Transmission } from "../../../../links.model";

/**
 * Serviço responsável por tratar Slots e Portas de um End de um link.
 */
@Injectable({
    providedIn: "root"
})
export class LinkDeviceSlotPortService {

    /**
     * Valor default para ID de slots não selecionados.
     * Esse caso ocorre quando um Mux/Demux é selecionado ou durante a criação de um novo link
     * (quando os dados ainda estão sendo carregados na tela).
     *
     * O Slot é tratado como uma string, pois dependendo do tipo do equipamento, seu valor pode ser um nome,
     * como "PSU1" por exemplo.
     */
    public readonly INVALID_SLOT: string = "-1";

    /**
     * Valor default para ID de portas não selecionados.
     * Esse caso ocorre quando um Mux/Demux é selecionado ou durante a criação de um novo link
     * (quando os dados ainda estão sendo carregados na tela).
     *
     * A porta é tratada como um número inteiro, pois independente do tipo do equipamento,
     * seu valor é sempre um número.
     */
    public readonly INVALID_PORT: number = -1;

    /**
     * Valor default para chassis.
     * Esse caso ocorre quando um Mux/Demux é selecionado ou durante a criação de um novo link
     * (quando os dados ainda estão sendo carregados na tela).
     *
     * O chassis é tratado como uma string.
     */
    public readonly INVALID_CHASSIS: string = null;

    /**
     * Valor default para o portModelId.
     * Esse caso ocorre quando um Mux/Demux é selecionado ou durante a criação de um novo link
     * (quando os dados ainda estão sendo carregados na tela).
     *
     * O portModelId é tratado como uma string.
     */
    public readonly INVALID_PORT_MODEL_ID: string = null;

    constructor(private readonly linkDeviceEndTransmissionService: LinkDeviceEndTransmissionService) {
        // Construtor intencionalmente vazio
    }

    initSlotPorts(slots: Slot[], endInputs: EndInputs, selectedEnd: SelectedEnd, endLink?: EndPortLink) {
        endInputs.endSlots = slots;
        selectedEnd.endSlotSelected = this.getSelectedSlot(endInputs.endSlots, endLink?.endSlotId);
        endInputs.endPorts = selectedEnd.endSlotSelected.ports;
        selectedEnd.endPortSelected = this.getSelectedPort(endInputs.endSlots, endInputs.endPorts,
            endLink?.endPortModelId, endLink?.endPortNo);

        if (selectedEnd.endPortSelected.txTransmission) {
            this.linkDeviceEndTransmissionService
                .updateTransmissionLinked(endInputs, selectedEnd.endPortSelected, Transmission.TX);
        }

        if (selectedEnd.endPortSelected.rxTransmission) {
            this.linkDeviceEndTransmissionService
                .updateTransmissionLinked(endInputs, selectedEnd.endPortSelected, Transmission.RX);
        }

        if (endLink) {
            const foundTransmission = endInputs.endTransmissions
                .find(transmission => transmission.transmission === endLink.endTransmission);

            if (foundTransmission) {
                selectedEnd.endTransmission = foundTransmission
            }
        }
    }

    handleSlotChanged(selectedEnd: SelectedEnd, endInputs: EndInputs, transmissionValue: Transmission, slot: Slot) {
        selectedEnd.endSlotSelected = slot;
        endInputs.endPorts = slot.ports;
        selectedEnd.endPortSelected = slot.ports[0];

        if (selectedEnd.endPortSelected.txTransmission) {
            this.linkDeviceEndTransmissionService
                .updateTransmissionLinked(endInputs, selectedEnd.endPortSelected, Transmission.TX);
        }

        if (selectedEnd.endPortSelected.rxTransmission) {
            this.linkDeviceEndTransmissionService
                .updateTransmissionLinked(endInputs, selectedEnd.endPortSelected, Transmission.RX);
        }

        selectedEnd.endTransmission = endInputs.endTransmissions
            .find(transmission => transmission.transmission === transmissionValue);
    }

    /**
     * Retorna o slot selecionado em um End.
     *
     * O slotId possui como valor default "-1", indicando que nenhum slot foi selecionado previamente. Este caso
     * ocorre na criação de um link e na edição de um link que possui um Mux/Demux como End.
     */
    private getSelectedSlot(slots: Slot[], slotId: string = this.INVALID_SLOT) {
        if (slotId === this.INVALID_SLOT) {
            return slots[0];
        }

        return slots.find((slot) => slot.slotNo === slotId);
    }

    /**
     * Retorna a porta selecionado em um End.
     *
     * O portId possui como valor default "-1", indicando que nenhuma porta foi selecionada previamente. Este caso
     * ocorre na criação de um link e na edição de um link que possui um Mux/Demux como End.
     */
    private getSelectedPort(slots: Slot[], ports: Port[], portModelId: string, portId: number = this.INVALID_PORT) {
        if (portId === this.INVALID_PORT) {
            return slots[0].ports[0];
        }

        return ports.find((port) => port.portNo === portId && port.portModelId === portModelId);
    }
}
